import { AlertTriangle, Check, InfoIcon, Loader2, Minus, Plus, Trash, X, XIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'src/components/ui/alert-dialog'
import { Button } from 'src/components/ui/button'
import { Label } from 'src/components/ui/label'
import { Textarea } from 'src/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/ui/tooltip'
import { CONFIG } from 'src/config'
import lodash from 'lodash'
import toast from 'react-hot-toast'
import { getWindevLang } from 'src/lib/lang'
import { Input } from 'src/components/ui/input';
import axios from 'src/axios/axios';
import { useMutation } from 'react-query';

type P = {
    trigger?: React.ReactNode
    docids?: number[]
    token?: string
    disabled?: boolean
    verification_settings?: any;
    metatypes?: any
    onSuccess?: () => void
}

type Meta = {
    metatype: string;
    metavalue: string;
}

const RejectBulk = ({trigger, docids, disabled=false, verification_settings, metatypes, token, onSuccess}:P) => {
    const { t, i18n } = useTranslation()

    const [open, setopen] = useState(false);

    const settings = verification_settings?.metadata;
    const groups:any[] = CONFIG.workflowGroups

    const [values, setValues] = useState<Meta[][]>([]);
    const [remarks, setremarks] = useState<{[key:string]:string}>({});

    useEffect(() => {
        if(!verification_settings) return;
        if(!settings) return;

        const initialValues: Meta[][] = [];
        const addedGroups: string[] = [];

        // Loop through settings array and create initial values state based on groups
        settings.forEach((setting:any) => {
            if(setting.metatype === "0"){ return; }
            const group = groups.find(g => g.includes(setting.metatype));
            if (group && !addedGroups.includes(group.join(''))) {
                // If the setting belongs to a group and the group hasn't been added yet, add a row with items of that group
                initialValues.push(group.map((metatype:any) => ({ metatype, metavalue: '' })));
                addedGroups.push(group.join(''));
            } else if (!group) {
                // If the setting does not belong to any group, add a row with that single setting
                initialValues.push([{ ...setting, metavalue: '' }]);
            }
        });

        setValues(initialValues);
    }, [verification_settings]);

    const { mutate:handleSendFlow, reset, isError, isLoading, isSuccess } = useMutation({
        mutationFn: async () => {
            window.localStorage.removeItem(`values`)

            let metadata:{metatype:string, metavalue:string}[] = []
            
            if(CONFIG.workflowGroupsFitdoc === "trailing-type"){
                metadata = transformTrailingTypeMetadata([...values])
            }
            else if(CONFIG.workflowGroupsFitdoc === "prefix-value"){
                metadata = values.map((x, i)=>(x.map((y)=>({metatype:y.metatype, metavalue:`[${i}] - ${y.metavalue}`})))).flat()
            }
            else{
                metadata = values.map((x, i)=>(x.map((y)=>({metatype:y.metatype, metavalue:`[${i}] - ${y.metavalue}`})))).flat()
            }

            const { data } = await axios.post(`wf/verify`, {
                token: token,
                workflows: docids?.map((docid)=>({
                    docid:docid, 
                    remark:remarks[docid], 
                    state:0,
                    metadata:metadata
                }))
            })
            if(data.errorcode!==0){ throw new Error(data.message) }


            toast.success(t('Succesvol afgekeurd'), {id:'wf'})
            onSuccess&&onSuccess()
            handleCloseSheet()
            return true;
        },
        onError: (e:any) => { 
            toast.error(`${t('Er liep iets mis')}`, {id:'wf'})
            setTimeout(()=>{ reset() }, 2000)
        },
        onSuccess: () => {
            setValues([])
            setremarks({})
            window.localStorage.removeItem(`values`)
            reset()
        }
    })

    function handleCloseSheet () {
        setopen(false);
        setremarks({});
        setValues([]);
        window.localStorage.removeItem(`values`)
    }

    function changeRemark (docid: number, value: string) {
        setremarks(p=>{
            let u = {...p}
            u[docid.toString()] = value
            return u
        })
    }

    function transformTrailingTypeMetadata(values:Meta[][],metatypeGroups=CONFIG.workflowGroups) {
        // Flatten the array
        const flatValues = values.flat();

        // Initialize counters for each group
        const counters:any = {};
        metatypeGroups.forEach(group => {
            group.forEach(metatype => {
                counters[metatype] = 0;
            });
        });

        // Helper function to get the group for a metatype
        function getGroup(metatype:string) {
            for (let group of metatypeGroups) {
                if (group.includes(metatype)) {
                    return group;
                }
            }
            return null;
        }

        // Transform the values
        const transformed = flatValues.map(item => {
            const group = getGroup(item.metatype);
            if (group) {
                counters[item.metatype]++;
                return {
                    ...item,
                    metatype: `${item.metatype}${counters[item.metatype]}`
                };
            } else {
                return item;
            }
        });

        return transformed;
    }

    const handleAddRow = (rowIndex: number) => {
        const newRow: Meta[] = [];
        values[rowIndex].forEach(meta => {
            newRow.push({ ...meta, metavalue:'' }); // Create new instances of each meta object
        });

        const count = countGroups(newRow.map(r => r.metatype))
        if(count === CONFIG.maxWorkflowClones){ toast.error('Maximum duplicates bereikt'); return; }

        setValues([...values, newRow]);
    }

    const handleRemoveRow = (index: number) => {
        const newValues = [...values];
        newValues.splice(index, 1);
        setValues(newValues);
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, rowIndex: number, metaIndex: number) => {
        const newValues = [...values];
        newValues[rowIndex][metaIndex].metavalue = event.target.value;
        setValues(newValues);
    }

    function convertToName(metatype:string) {
        return metatypes?.find((i:any)=>i.metatype===metatype)?.metatype_oms.find((i:any)=>i.nationid===getWindevLang(i18n.language))?.oms || metatype
    }

    function metatypeToInputType(metatype:string) {
        let type = 'text'
        switch (metatypes?.find((m:any)=>m.metatype===metatype)?.datatype) {
            case 'date':
                type = 'date'
                break;
            case 'string':
                type = 'text'
                break;
            case 'integer':
                type = 'number'
                break;
            case 'currency':
                type = 'number'
                break;
            default:
                break;
        }
        return type
    }

    function countGroups (metagroup:string[]) {
        let amount = 0;

        values.forEach((row) => {
            const rowGroup = row.map((r)=>r.metatype)
            if(lodash.isEqual(metagroup, rowGroup)){
                amount++
            }
        })

        return amount
    }

    const isFormComplete = (values: Meta[][]): boolean => {
        const required = settings?.filter((i:any)=>i.required).map((v:any)=>v.metatype) || []

        for (const row of values) {
            for (const meta of row) {
                if (required.includes(meta.metatype) && meta.metavalue === '') {
                    return false; // Required metatype found without a value
                }
            }
        }

        if(CONFIG.workflow.reject.requiredRemark){
            const filledRemarks = Object.values(remarks).filter(v=>v.length>0);
            if(filledRemarks.length !== docids?.length){ return false }
        }

        return true; // All required metatypes have values
    };
    
  return (
    <AlertDialog open={open} onOpenChange={(o)=>{setopen(o)}}>
        <AlertDialogTrigger asChild>
            {trigger ? trigger : <div><Tooltip><TooltipTrigger asChild><button disabled={disabled || docids?.length === 0} className='w-10 h-10 min-w-10 min-h-10 rounded-sm flex items-center justify-center hover:bg-slate-100 disabled:opacity-50'><XIcon className='w-4 h-4 text-red-600'/></button></TooltipTrigger><TooltipContent>{t('Document afkeuren')}</TooltipContent></Tooltip></div>}
        </AlertDialogTrigger>
        <AlertDialogContent className='max-h-[95dvh] overflow-auto max-w-5xl w-full'>
            <div className='grid md:grid-cols-2 gap-6'>
                <div>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Documenten afkeuren</AlertDialogTitle>
                        <AlertDialogDescription>U staat op het punt om <span className='font-semibold'>{docids?.length} documenten</span> af te keuren.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div>
                        {values.map((row, rowIndex) => (
                            <div key={`${row[0].metatype}${rowIndex}`} className='flex items-center justify-between gap-4 border-b py-3'>
                                <div className='flex overflow-auto flex-wrap gap-2 p-1 w-full'>
                                    {row.filter(i=>metatypes?.some((m:any)=>m.metatype===i.metatype)).map((meta, metaIndex) => {
                                    return (
                                        <div key={`${meta.metatype}-${metaIndex}`} className='flex-[1_1_40%] box-border'>
                                            <Label>{convertToName(meta.metatype)} {(settings?.find((s:any) => s.metatype===meta.metatype)?.required) && <span className='text-red-600'>*</span>}</Label>
                                            <Input 
                                                type={metatypeToInputType(meta.metatype)} 
                                                value={meta.metavalue}
                                                onChange={(event) => handleInputChange(event, rowIndex, metaIndex)}
                                            />
                                        </div>
                                    )}
                                    )}
                                </div>
                                
                                <div className='flex items-center'>
                                    {!(countGroups(row.map((r)=>r.metatype)) === CONFIG.maxWorkflowClones) &&
                                    <button onClick={() => handleAddRow(rowIndex)} className='w-8 h-8 rounded-full flex items-center justify-center text-green-600 hover:text-white hover:bg-green-600'><Plus className='w-4 h-4'/></button>
                                    }
                                    {(values.findIndex(v=>v[0].metatype===row[0].metatype) !== rowIndex) && <button onClick={() => handleRemoveRow(rowIndex)} className='w-8 h-8 rounded-full flex items-center justify-center text-red-600 hover:text-white hover:bg-red-600'><Trash className='w-4 h-4'/></button>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className='border-t md:border-t-0 md:border-l border-dashed pt-6 md:pl-6 md:pt-0 flex flex-col gap-4'>
                    <p className='font-semibold text-lg'>Opmerkingen</p>
                    <div className='flex flex-col gap-4'>
                        {docids && docids.map((docid,i)=>(
                            <div className='flex flex-col gap-1' key={`remark-${docid}`}>
                                <div className='flex items-center justify-between'>
                                    <Label>{t('Opmerking')} - Document {docid} {CONFIG.workflow.reject.requiredRemark && <span className='text-red-600'>*</span>}</Label>
                                </div>
                                
                                <Textarea onChange={({currentTarget})=>{changeRemark(docid, currentTarget.value)}} value={remarks[docid]||''} rows={1} className='h-10 min-h-10'/>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <Button onClick={()=>{handleSendFlow()}} disabled={!isFormComplete(values) || isError || isLoading || isSuccess} className='gap-2'>
                    {isSuccess && <Check className='w-4 h-4'/>}
                    {isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}
                    {isError && <AlertTriangle className='w-4 h-4'/>}
                    {t('Afkeuren')}
                </Button>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default RejectBulk